import { useEffect, useState } from "react";
import operatorApiService from "../../../../services/operator-api.service";
import editIcon from "../../../../assets/imgs/master/edit_icon_wh.svg";
import deleteIcon from "../../../../assets/imgs/master/delete_icon_wh.svg";

interface VehicleLabelProps {
  baseId: number | null;
}

interface EditableVehicle {
  vehicle_id: number | null;
  vehicle_label: string;
  vehicle_no: string;
  vehicle_communication_code: string;
  zao_suid: string;
  audio_alive_freq: number;
  selected_bitrate: number;
  unselected_bitrate: number;
  selected_framerate: number;
  unselected_framerate: number;
  vehicle_model_id: number;
  vehicle_model_name?: string;
  base_id: number;
  status: "original" | "edited" | "new" | "deleted";
  isEditing?: boolean;
  internalId: string;
}

interface VehicleModelOption {
  vehicle_model_id: number;
  vehicle_model_name: string;
}

const VehicleLabel = ({ baseId }: VehicleLabelProps) => {
  const [originalRows, setOriginalRows] = useState<EditableVehicle[]>([]);
  const [rows, setRows] = useState<EditableVehicle[]>([]);
  const [vehicleModels, setVehicleModels] = useState<VehicleModelOption[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!baseId) return;

    const fetchData = async () => {
      const [vehicles, models] = await Promise.all([
        operatorApiService.doGetVehicles({ baseId }),
        operatorApiService.doGetVehicleModels({ baseId }),
      ]);

      setVehicleModels(models);

      const initial = vehicles.map((entry: any) => ({
        ...entry,
        status: "original",
        isEditing: false,
        internalId: `existing-${entry.vehicle_id}`,
      }));

      setOriginalRows(initial);
      setRows(initial);
    };

    fetchData();
  }, [baseId]);

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        vehicle_id: null,
        vehicle_label: "",
        vehicle_no: "",
        vehicle_communication_code: "",
        zao_suid: "",
        audio_alive_freq: 0,
        selected_bitrate: 2000,
        unselected_bitrate: 200,
        selected_framerate: 30,
        unselected_framerate: 5,
        vehicle_model_id: vehicleModels[0]?.vehicle_model_id || 0,
        base_id: baseId!,
        status: "new",
        isEditing: true,
        internalId: `new-${Date.now()}-${Math.random()}`,
      },
    ]);
  };

  const markDeleted = (internalId: string) => {
    setRows((prev) =>
      prev
        .filter((r) => {
          if (r.internalId !== internalId) return true;
          return r.status !== "new";
        })
        .map((r) =>
          r.internalId === internalId && r.status !== "new"
            ? { ...r, status: "deleted" }
            : r
        )
    );
  };

  const toggleEdit = (internalId: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.internalId === internalId
          ? {
              ...r,
              isEditing: true,
              status: r.status === "original" ? "edited" : r.status,
            }
          : r
      )
    );
  };

  const handleChange = (
    index: number,
    field: keyof EditableVehicle,
    value: any
  ) => {
    const updated = [...rows];
    (updated[index] as any)[field] = value;
    setRows(updated);
  };

  const submit = async () => {
    try {
      const hasBlank = rows.some(
        (r) =>
          (r.status === "new" || r.status === "edited") &&
          (r.vehicle_no.trim() === "" ||
            r.vehicle_label.trim() === "" ||
            r.vehicle_communication_code.trim() === "")
      );

      if (hasBlank) {
        setErrorMessage("空欄があります。");
        setTimeout(() => setErrorMessage(null), 3000);
        return;
      }

      const hasTooLong = rows.some(
        (r) =>
          (r.status === "new" || r.status === "edited") &&
          (r.vehicle_no.trim().length > 2 ||
            r.vehicle_label.trim().length > 5 ||
            r.vehicle_communication_code.trim().length > 8 ||
            (r.zao_suid?.trim()?.length ?? 0) > 12)
      );

      if (hasTooLong) {
        setErrorMessage("文字数が制限を超えています。");
        setTimeout(() => setErrorMessage(null), 3000);
        return;
      }

      const create = rows
        .filter((r) => r.status === "new")
        .map(
          ({
            vehicle_label,
            vehicle_no,
            vehicle_communication_code,
            zao_suid,
            audio_alive_freq,
            selected_bitrate,
            unselected_bitrate,
            selected_framerate,
            unselected_framerate,
            vehicle_model_id,
          }) => ({
            vehicle_label,
            vehicle_no,
            vehicle_communication_code,
            zao_suid,
            audio_alive_freq,
            selected_bitrate,
            unselected_bitrate,
            selected_framerate,
            unselected_framerate,
            vehicle_model_id,
            base_id: baseId!,
          })
        );

      const update = rows
        .filter((r) => r.status === "edited")
        .map(
          ({
            vehicle_id,
            vehicle_label,
            vehicle_no,
            vehicle_communication_code,
            zao_suid,
            audio_alive_freq,
            selected_bitrate,
            unselected_bitrate,
            selected_framerate,
            unselected_framerate,
            vehicle_model_id,
          }) => ({
            vehicle_id,
            vehicle_label,
            vehicle_no,
            vehicle_communication_code,
            zao_suid,
            audio_alive_freq,
            selected_bitrate,
            unselected_bitrate,
            selected_framerate,
            unselected_framerate,
            vehicle_model_id,
          })
        );

      const del = rows
        .filter((r) => r.status === "deleted")
        .map((r) => r.vehicle_id);

      if (create.length === 0 && update.length === 0 && del.length === 0) {
        return;
      }

      const response = await operatorApiService.saveVehicles({
        baseId,
        create,
        update,
        delete: del,
      });

      if (response.status === "success") {
        setSuccessMessage("更新に成功しました。");
        setTimeout(() => setSuccessMessage(null), 3000);

        const refreshed = await operatorApiService.doGetVehicles({ baseId });
        const mapped = refreshed.map((entry: any) => ({
          ...entry,
          status: "original",
          isEditing: false,
          internalId: `existing-${entry.vehicle_id}`,
        }));
        setRows(mapped);
        setOriginalRows(mapped);
      }
    } catch (error) {
      setErrorMessage("更新に失敗しました。");
      setTimeout(() => setErrorMessage(null), 3000);
    }
  };

  const handleResetAll = () => {
    const reset = originalRows.map((r) => ({ ...r }));
    setRows(reset);
  };

  return (
    <div className="section">
      <div className="sectionTitle">車両一覧</div>
      <div className="sectionBody">
        <table className="tableArea vehicleList">
          <thead>
            <tr>
              <th>車種名</th>
              <th>車両番号</th>
              <th>車両表示名</th>
              <th>車両識別コード</th>
              <th>SUID</th>
              <th>音声死活用周波数</th>
              <th>選択時TBR</th>
              <th>非選択時TBR</th>
              <th>選択時FRM</th>
              <th>非選択時FRM</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.internalId}>
                <td
                  className={`wideCol ${
                    row.status === "deleted" ? "deleteCell" : ""
                  }`}
                >
                  {row.isEditing ? (
                    <select
                      value={row.vehicle_model_id}
                      onChange={(e) =>
                        handleChange(
                          i,
                          "vehicle_model_id",
                          Number(e.target.value)
                        )
                      }
                    >
                      {vehicleModels.map((vm) => (
                        <option
                          key={vm.vehicle_model_id}
                          value={vm.vehicle_model_id}
                        >
                          {`${vm.vehicle_model_name}`}
                        </option>
                      ))}
                    </select>
                  ) : (
                    `${row.vehicle_model_name ?? ""}`
                  )}
                </td>
                <td className={row.status === "deleted" ? "deleteCell" : ""}>
                  {row.isEditing ? (
                    <input
                      value={row.vehicle_no}
                      onChange={(e) =>
                        handleChange(i, "vehicle_no", e.target.value)
                      }
                    />
                  ) : (
                    row.vehicle_no
                  )}
                </td>
                <td className={row.status === "deleted" ? "deleteCell" : ""}>
                  {row.isEditing ? (
                    <input
                      value={row.vehicle_label}
                      onChange={(e) =>
                        handleChange(i, "vehicle_label", e.target.value)
                      }
                    />
                  ) : (
                    row.vehicle_label
                  )}
                </td>
                <td className={row.status === "deleted" ? "deleteCell" : ""}>
                  {row.isEditing ? (
                    <input
                      value={row.vehicle_communication_code}
                      onChange={(e) =>
                        handleChange(
                          i,
                          "vehicle_communication_code",
                          e.target.value
                        )
                      }
                    />
                  ) : (
                    row.vehicle_communication_code
                  )}
                </td>
                <td className={row.status === "deleted" ? "deleteCell" : ""}>
                  {row.isEditing ? (
                    <input
                      value={row.zao_suid}
                      onChange={(e) =>
                        handleChange(i, "zao_suid", e.target.value)
                      }
                    />
                  ) : (
                    row.zao_suid
                  )}
                </td>
                <td className={row.status === "deleted" ? "deleteCell" : ""}>
                  {row.isEditing ? (
                    <input
                      value={row.audio_alive_freq}
                      onChange={(e) =>
                        handleChange(i, "audio_alive_freq", e.target.value)
                      }
                    />
                  ) : (
                    row.audio_alive_freq
                  )}
                </td>
                <td className={row.status === "deleted" ? "deleteCell" : ""}>
                  {row.isEditing ? (
                    <input
                      value={row.selected_bitrate}
                      onChange={(e) =>
                        handleChange(i, "selected_bitrate", e.target.value)
                      }
                    />
                  ) : (
                    row.selected_bitrate
                  )}
                </td>
                <td className={row.status === "deleted" ? "deleteCell" : ""}>
                  {row.isEditing ? (
                    <input
                      value={row.unselected_bitrate}
                      onChange={(e) =>
                        handleChange(i, "unselected_bitrate", e.target.value)
                      }
                    />
                  ) : (
                    row.unselected_bitrate
                  )}
                </td>
                <td className={row.status === "deleted" ? "deleteCell" : ""}>
                  {row.isEditing ? (
                    <input
                      value={row.selected_framerate}
                      onChange={(e) =>
                        handleChange(i, "selected_framerate", e.target.value)
                      }
                    />
                  ) : (
                    row.selected_framerate
                  )}
                </td>
                <td className={row.status === "deleted" ? "deleteCell" : ""}>
                  {row.isEditing ? (
                    <input
                      value={row.unselected_framerate}
                      onChange={(e) =>
                        handleChange(i, "unselected_framerate", e.target.value)
                      }
                    />
                  ) : (
                    row.unselected_framerate
                  )}
                </td>
                <td>
                  {row.status === "deleted" ? (
                    <span className="deletionLabel">削除予定</span>
                  ) : row.status === "edited" ? (
                    <span className="editLabel">編集予定</span>
                  ) : row.status === "new" ? (
                    <button
                      className="cancelButton"
                      onClick={() => markDeleted(row.internalId)}
                    >
                      取消
                    </button>
                  ) : (
                    <div className="buttonGroup">
                      <button
                        className="updateButton"
                        onClick={() => toggleEdit(row.internalId)}
                      >
                        <img src={editIcon} alt="" className="icon" />
                        編集
                      </button>
                      <button
                        className="deleteButton"
                        onClick={() => markDeleted(row.internalId)}
                      >
                        <img src={deleteIcon} alt="" className="icon" />
                        削除
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="createFooter">
          <div className="createLabel">
            表に新規入力欄が追加されます。新しい車両情報を入力し、登録してください。
          </div>
          <button className="createButton" onClick={addRow}>
            新規行追加
          </button>
        </div>
        {successMessage && (
          <div className="successMessage">{successMessage}</div>
        )}
        {errorMessage && <div className="errorMessage">{errorMessage}</div>}
        {rows.some((r) => r.status !== "original") && (
          <div className="actionButtons">
            <button className="backButton" onClick={handleResetAll}>
              戻る
            </button>
            <button className="decisionButton" onClick={submit}>
              確定
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleLabel;
